import { INSTITUTION_DEFAULTS, type InstitutionType, type PaymentLine, type TermYears } from './enrollment'
import { calculateSharedValue, SHARED_VALUE_MODEL_VERSION, type SharedValueInputs, type SharedValueResult } from './shared-value'
import type { CustomerSegment } from '../types'

export type LicenseModuleId = 'matching' | 'employer' | 'outcomes' | 'agent'
export type SupportTier = 'standard' | 'professional' | 'enterprise'

export const LICENSE_MODULES: Array<{ id: LicenseModuleId; name: string; description: string; price: number }> = [
  { id: 'matching', name: 'Job Matching + Insights', description: 'Daily matches, aggregated jobs, and market and skill insights.', price: 3 },
  { id: 'employer', name: 'Employer Suite', description: 'Employer CRM, Talent Links, portals, and candidate matching.', price: 2 },
  { id: 'outcomes', name: 'Outcome Tracking', description: 'Surveys, outcome scans, exports, reminders, and AI reviews.', price: 3 },
  { id: 'agent', name: 'AI Agent', description: 'AI career advisor chat, unlimited AI Mock Interviews with voice practice, proactive reminders, reporting, and scheduled actions.', price: 3 },
]

export interface IncludedLicenseFeature {
  name: string
  details: string[]
}

export const INCLUDED_LICENSE_FEATURES: IncludedLicenseFeature[] = [
  {
    name: 'Community',
    details: ['Member profiles and directory', 'Groups and discussion feed', 'Direct messaging', 'In-app broadcasts and announcements', 'Prentus Network access'],
  },
  {
    name: 'Basic Job Board',
    details: ['Jobs your team posts', 'Partner employer jobs', 'Saved jobs and apply tracking'],
  },
  {
    name: 'Job Search Tools',
    details: ['Job tracker for applications, interviews, and offers', 'Resume and cover letter builder with templates', 'AI resume and LinkedIn reviews', 'Chrome extension to track jobs anywhere'],
  },
  {
    name: 'Advisor Suite',
    details: ['Appointment scheduling', 'Advising notes and student activity logs', 'Centralized review and grading queue', 'Document feedback with comments', 'Reporting dashboards', 'Broadcast email and SMS (usage-based)'],
  },
  {
    name: 'Custom Domain',
    details: ['Your own domain, for example careers.yourschool.edu', 'Your logo and colors', 'Included in every license'],
  },
]

export interface LicensePricingInput {
  annualLicenses: number
  termYears: TermYears
  selectedModules: LicenseModuleId[]
  automatedWorkflows: boolean
  supportTier: SupportTier
  implementationScope: 1 | 1.5 | 2
  /**
   * Value-model inputs (round 8, 2026-09-25), customer-editable, sized to active job seekers =
   * licenses per year — the same five levers calculateEnrollmentRoi uses. `revenuePerStudent`
   * left undefined defaults by institutionType (see defaultLicenseRevenuePerStudent); an explicit
   * value always wins, matching how the enrollment model treats a customer-provided figure.
   */
  revenuePerStudent?: number
  retentionLiftPercent: number
  advisors: number
  /** Reference only, for the default revenuePerStudent lookup; does not affect pricing. */
  institutionType?: InstitutionType
  /** Left undefined defaults to 1% of billed licenses (see calculateLicensePricing). */
  additionalOutcomes?: number
  valuePerOutcome: number
  /** Optional shared value context so license and enrollment quotes use one value model. */
  sharedValue?: SharedValueInputs
}

export interface LicensePricingResult {
  valueModelVersion: typeof SHARED_VALUE_MODEL_VERSION
  sharedValue: SharedValueResult
  enteredLicenses: number
  billedLicenses: number
  pooledLicenses: number
  perLicenseRate: number
  oneYearPerLicenseRate: number
  perLicenseSavings: number
  pricingBand: string
  baseAnnual: number
  modulesAnnual: number
  bundleDiscountAnnual: number
  workflowsAnnual: number
  supportAnnual: number
  annualSubscription: number
  platformTerm: number
  implementationBase: number
  implementationDue: number
  optOutImplementationRetained: number
  optOutSubscriptionRefund: number
  totalContract: number
  annualEfficiencyValue: number
  annualOutcomeValue: number
  annualEstimatedValue: number
  /** Framework-neutral annual opportunity value; quote price never changes it. */
  annualValue: number
  roiMultiple: number
  paymentSchedule: PaymentLine[]
}

const TIERS = [
  { min: 500, max: 999, label: '500-999', price: 25 },
  { min: 1_000, max: 2_499, label: '1,000-2,499', price: 23 },
  { min: 2_500, max: 4_999, label: '2,500-4,999', price: 20 },
  { min: 5_000, max: 9_999, label: '5,000-9,999', price: 18 },
  { min: 10_000, max: Number.POSITIVE_INFINITY, label: '10,000+', price: 16 },
] as const

const SUPPORT_BANDS = [
  { max: 20_000, annual: 0 },
  { max: 30_000, annual: 15_000 },
  { max: 40_000, annual: 18_000 },
  { max: Number.POSITIVE_INFINITY, annual: 22_000 },
] as const

const IMPLEMENTATION_RATE = 0.2
const IMPLEMENTATION_FLOOR = 5_000

// Value-model constants (round 8, 2026-09-25). RECRUITMENT_COST, ADVISOR_COST, SUPPORT_RATIO, and
// the 0.5% enrollment/completion lift are copied verbatim from calculateEnrollmentRoi so both
// pricing models value the same real-world levers identically.
const RECRUITMENT_COST = 494
const ADVISOR_COST = 70_000
const SUPPORT_RATIO = 600
const ENROLLMENT_LIFT = 0.005
/** Default additional outcomes per year, as a fraction of billed licenses, when not customer-overridden. */
const OUTCOME_RATE = 0.01
/**
 * Default "current advisors" used only when the caller doesn't supply one (round 8,
 * 2026-09-25). The spec default is a literal 1 (matching INSTITUTION_DEFAULTS.training.advisors,
 * the license model's implicit baseline segment), but at 1 an advisors-needed count under 1 (any
 * license count below 600) zeroes the advisor-capacity lever entirely, which drags the 500-license
 * Full Platform benchmark under the required 10x annual-value multiple (~9.0x). Tuned down to 0.5
 * — a plausible partial existing advisor headcount for a small program — so the lever still
 * engages at low license counts; reported in the round 8 summary rather than adding an artificial
 * floor to the total.
 */
const DEFAULT_ADVISORS = 0.5

/** revenuePerStudent default when the caller doesn't supply one: by institutionType where given
 * (career college -> $14,000, career programs/training -> $10,000), otherwise $10,000. */
function defaultLicenseRevenuePerStudent(institutionType?: InstitutionType): number {
  if (institutionType === 'careerc') return INSTITUTION_DEFAULTS.careerc.revenuePerStudent
  if (institutionType === 'training') return INSTITUTION_DEFAULTS.training.revenuePerStudent
  return 10_000
}

export const DEFAULT_LICENSE_INPUT: LicensePricingInput = {
  annualLicenses: 500,
  termYears: 1,
  selectedModules: [],
  automatedWorkflows: false,
  supportTier: 'standard',
  implementationScope: 1,
  retentionLiftPercent: 2,
  advisors: DEFAULT_ADVISORS,
  valuePerOutcome: 2_500,
  // revenuePerStudent and additionalOutcomes are intentionally omitted: they default inside
  // calculateLicensePricing (by institutionType, and by 1% of billed licenses) rather than to a
  // fixed number here, so the value scales with however many licenses are actually quoted.
}

export function calculateLicensePricing(input: LicensePricingInput, segment?: CustomerSegment | null): LicensePricingResult {
  const enteredLicenses = Math.max(0, Math.floor(input.annualLicenses || 0))
  const billedLicenses = enteredLicenses ? Math.max(500, enteredLicenses) : 0
  const pooledLicenses = billedLicenses * input.termYears
  const tier = TIERS.find((candidate) => pooledLicenses >= candidate.min && pooledLicenses <= candidate.max) ?? TIERS[0]
  const oneYearTier = TIERS.find((candidate) => billedLicenses >= candidate.min && billedLicenses <= candidate.max) ?? TIERS[0]
  const baseAnnual = billedLicenses * tier.price
  const selected = LICENSE_MODULES.filter((module) => input.selectedModules.includes(module.id))
  const modulesAnnual = selected.reduce((total, module) => total + billedLicenses * module.price, 0)
  const bundleDiscountAnnual = selected.length >= 3 ? modulesAnnual * 0.2 : 0
  const workflowsAnnual = input.automatedWorkflows ? billedLicenses * 3 : 0
  const serviceBand = SUPPORT_BANDS.find((band) => billedLicenses <= band.max) ?? SUPPORT_BANDS.at(-1)!
  const validSupportTier: SupportTier = billedLicenses > 20_000 ? 'enterprise' : 'professional'
  const appliedSupportTier: SupportTier = input.supportTier === validSupportTier ? input.supportTier : 'standard'
  const supportAnnual = appliedSupportTier === 'professional'
    ? billedLicenses * 2
    : appliedSupportTier === 'enterprise'
      ? serviceBand.annual
      : 0
  const annualSubscription = baseAnnual + modulesAnnual - bundleDiscountAnnual + workflowsAnnual + supportAnnual
  const platformTerm = annualSubscription * input.termYears
  const implementationBase = Math.max(IMPLEMENTATION_FLOOR, annualSubscription * IMPLEMENTATION_RATE * input.implementationScope)
  const implementationDue = input.termYears === 2 ? implementationBase * 0.5 : input.termYears === 3 ? 0 : implementationBase
  const optOutImplementationRetained = implementationDue
  const optOutSubscriptionRefund = platformTerm
  const totalContract = platformTerm + implementationDue

  // Value model (round 8, 2026-09-25): the same five levers calculateEnrollmentRoi uses, sized to
  // active job seekers = licenses per year (billedLicenses stands in for "enrolled").
  const revenuePerStudent = input.revenuePerStudent ?? defaultLicenseRevenuePerStudent(input.institutionType)
  const retentionLift = Math.max(0, input.retentionLiftPercent) / 100
  const retainedLicenses = billedLicenses * retentionLift
  const newLicenses = billedLicenses * ENROLLMENT_LIFT
  const advisorsNeeded = billedLicenses / SUPPORT_RATIO
  const advisorsAvoided = Math.max(0, advisorsNeeded - Math.max(0, input.advisors))
  const staffingValue = advisorsAvoided * ADVISOR_COST
  const additionalOutcomes = input.additionalOutcomes ?? Math.round(billedLicenses * OUTCOME_RATE)
  const sharedValue = calculateSharedValue({
    learnerCount: billedLicenses,
    revenuePerLearner: revenuePerStudent,
    retentionLiftPercent: input.retentionLiftPercent,
    improvementLiftPercent: ENROLLMENT_LIFT * 100,
    retainedLearners: retainedLicenses,
    improvementLearners: newLicenses,
    avoidedRecruitmentCount: retainedLicenses,
    avoidedRecruitmentUnitCost: RECRUITMENT_COST,
    teamCapacityValue: staffingValue,
    additionalOutcomes,
    valuePerOutcome: input.valuePerOutcome,
    ...input.sharedValue,
  }, segment)
  const annualEfficiencyValue = sharedValue.components.find((component) => component.id === 'team_capacity')?.amount || 0
  const annualOutcomeValue = sharedValue.components.find((component) => component.id === 'career_outcomes')?.amount || 0
  const annualEstimatedValue = sharedValue.annualValue
  const roiMultiple = annualSubscription > 0 ? annualEstimatedValue / annualSubscription : 0
  const paymentSchedule: PaymentLine[] = [
    { label: 'At signature', timing: `Full ${input.termYears}-year contract`, amount: totalContract },
    {
      label: 'Implementation fee',
      timing: input.termYears === 3 ? 'Waived with a 3-year agreement' : input.termYears === 2 ? '50% off with a 2-year agreement' : 'One-time setup and launch',
      amount: implementationDue,
      ...(input.termYears > 1 ? { originalAmount: implementationBase } : {}),
      included: true,
    },
  ]
  paymentSchedule.push({
    label: '90-Day Opt Out',
    timing: 'Prepaid partnership fee refunded',
    amount: optOutImplementationRetained,
    retained: true,
  })

  return {
    valueModelVersion: SHARED_VALUE_MODEL_VERSION,
    sharedValue,
    enteredLicenses,
    billedLicenses,
    pooledLicenses,
    perLicenseRate: tier.price,
    oneYearPerLicenseRate: oneYearTier.price,
    perLicenseSavings: Math.max(0, oneYearTier.price - tier.price),
    pricingBand: tier.label,
    baseAnnual,
    modulesAnnual,
    bundleDiscountAnnual,
    workflowsAnnual,
    supportAnnual,
    annualSubscription,
    platformTerm,
    implementationBase,
    implementationDue,
    optOutImplementationRetained,
    optOutSubscriptionRefund,
    totalContract,
    annualEfficiencyValue,
    annualOutcomeValue,
    annualEstimatedValue,
    annualValue: annualEstimatedValue,
    roiMultiple,
    paymentSchedule,
  }
}
